/* eslint-disable max-lines */
import React, { useEffect } from 'react'
import styled from 'styled-components'

import HealthStatusIcon, { HealthStatus } from 'pages/shared/HealthStatusIcon'
import ZoneStatus from 'pages/shared/DeviceTabs/ZoneStatus'
import ServiceContractIcon from 'pages/shared/DeviceTabs/ServiceContractIcon'

import Tooltip from 'components/Tooltip'

import { useStoreContext } from 'hooks/StoreContext'
import { useWebsocketContext } from 'hooks/WebsocketContext'
import { useFeaturesContext } from 'hooks/FeaturesContext'
import useNexiaStream from 'hooks/useNexiaStream'

import {
  updateAlertsSeverity,
  highestSeverityForIcon,
  highestSeverityByAlertCount
} from 'helpers/alerts'
import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { longDate, shortDate } from 'helpers/formatters'

import { DeviceBase } from 'types/Device'

import AlertIconWithCount from 'pages/shared/Alert/IconWithCount'

type Props = {
  customerName?: string
  device: DeviceBase
  isActive: boolean
}

const ALERT_ICON_CONFIG = [
  ['critical', 'criticalAlertCount'],
  ['critical-sda', 'sdaCriticalAlertCount'],
  ['major', 'majorAlertCount'],
  ['major-sda', 'sdaMajorAlertCount'],
  ['minor', 'minorAlertCount'],
  ['minor-sda', 'sdaMinorAlertCount']
] as const

const alertSeverityColorMap: Record<string, { active: string; inactive: string }> = {
  'critical': { active: 'healthCritical', inactive: 'healthCriticalInactive' },
  'critical-sda': { active: 'criticalSdaAlert', inactive: 'criticalSdaAlertInactive' },
  'major': { active: 'majorAlert', inactive: 'majorAlertInactive' },
  'major-sda': { active: 'majorSdaAlert', inactive: 'majorSdaAlertInactive' },
  'minor': { active: 'minorAlertTab', inactive: 'minorAlertTab' },
  'minor-sda': { active: 'minorSdaAlertTab', inactive: 'minorSdaAlertTab' },
  'fair': { active: 'healthFair', inactive: 'healthFairInactive' },
  'great': { active: 'healthGreat', inactive: 'healthGreatInactive' },
  'unknown': { active: 'healthUnknown', inactive: 'healthUnknown' }
}

const DeviceTabHeader = ({ customerName, device, isActive }: Props) => {
  const { reportCache } = useStoreContext()
  const { eventData: report } = useNexiaStream(
    device.deviceId,
    'alarms',
    reportCache,
    device.optedIn
  )
  const {
    subscribe,
    unsubscribe,
    payload: alertsReport
  } = useWebsocketContext(device.deviceId, 'alerts')
  const { enabledFeatures } = useFeaturesContext()

  const isHehm = device.deviceType.toLowerCase() === 'hehm'

  const activeReport = enabledFeatures.includes('montblanc_websockets')
    ? alertsReport
    : report

  const severity = isHehm
    ? device.healthScore
    : activeReport
      ? highestSeverityForIcon(
        updateAlertsSeverity(activeReport.alerts ?? [])
      )
      : highestSeverityByAlertCount(device)

  const alertsCount = activeReport?.alerts?.length ?? 0

  const hehmAlertCount = isHehm
    ? [
      device.criticalAlertCount,
      device.majorAlertCount,
      device.minorAlertCount,
      device.sdaCriticalAlertCount,
      device.sdaMajorAlertCount,
      device.sdaMinorAlertCount
    ].reduce((a: number, b) => a + (b ?? 0), 0)
    : 0 as const

  useEffect(() => {
    if (device.optedIn) {
      subscribe(device.deviceId, 'alerts')

      return () => {
        unsubscribe(device.deviceId, 'alerts')
      }
    }
  }, [device, subscribe, unsubscribe])

  const ServiceContractInfo = () => {
    const {
      serviceContractStatus,
      serviceContractStartAt,
      serviceContractEndAt
    } = device

    let contractInfo = <></>
    if (
      serviceContractStatus === 'Active' &&
      serviceContractStartAt &&
      serviceContractEndAt
    ) {
      contractInfo = (
        <S.Description>
          {shortDate(serviceContractStartAt)} -{' '}
          {shortDate(serviceContractEndAt)}
        </S.Description>
      )
    } else if (serviceContractStatus === 'Active' && serviceContractEndAt) {
      contractInfo = (
        <S.Description>
          Expires on {longDate(serviceContractEndAt)}
        </S.Description>
      )
    } else if (serviceContractStatus === 'Expired' && serviceContractEndAt) {
      contractInfo = (
        <S.Description>
          Expired on {longDate(serviceContractEndAt)}
        </S.Description>
      )
    }

    return (
      <>
        <S.ServiceContractWrapper>
          {serviceContractStatus && serviceContractStatus !== 'None'
            ? `Service Contract ${serviceContractStatus}`
            : 'No Service Contract'}
        </S.ServiceContractWrapper>
        <S.ContactInfo>{contractInfo}</S.ContactInfo>
      </>
    )
  }

  const AlertsInfo = () => (
    <S.AlertsWrapper>
      {alertsCount > 0 || hehmAlertCount > 0 ? (
        <S.AlertsDetails>
          {ALERT_ICON_CONFIG.map(([severity, key]) => (
            <AlertIconWithCount
              key={severity}
              severity={severity}
              count={device[key]}
            />
          ))}
        </S.AlertsDetails>
      ) : (
        <S.NoActiveAlerts>
          <S.NoAlertIcon
            data-testid="alert-icon"
            aria-label="no active alert"
            src="/img/alert-status-none.svg"
          />
          <S.Label>No Active Alerts</S.Label>
        </S.NoActiveAlerts>
      )}
    </S.AlertsWrapper>
  )

  const getDeviceDisplayModel = /hehm/i.test(device.deviceType) ? 'EHMKT' : device.deviceModel

  return (
    <S.Container>
      {
        device.registered && device.optedIn && (
        <S.AlertColor
          isActiveTab={isActive}
          severity={severity}
          aria-label={severity ? `highest alert severity: ${severity}` : 'no alerts'
          }
        />
        )
      }
      <S.Content>
        <S.Left>
          <S.Row>
            <Tooltip
              title={
                <S.TooltipContent>
                  <AlertsInfo />
                  {!isHehm && (
                    <S.Zone>
                      <S.Label>
                        <ZoneStatus device={device} />
                      </S.Label>
                    </S.Zone>
                  )}
                  <table>
                    <tbody>
                      <tr>
                        <td><S.Label>Model:</S.Label></td>
                        <td><S.Description>{ device.deviceModel }</S.Description></td>
                      </tr>
                      <tr>
                        <td><S.Label>AUID:</S.Label></td>
                        <td><S.Description>{device.deviceId}</S.Description></td>
                      </tr>
                      <tr>
                        <td><S.Label>S/N:</S.Label></td>
                        <td><S.Description>{device.serialNumber || '--'}</S.Description></td>
                      </tr>
                      {isHehm && (
                        <tr>
                          <td><S.Label>IMEI:</S.Label></td>
                          <td><S.Description>{device.imei || '--'}</S.Description></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {!isHehm && (<ServiceContractInfo />) }
                </S.TooltipContent>
              }
              placement="right"
            >
              <S.Name isActiveTab={isActive}>
                {device.name || device.deviceId}
              </S.Name>
            </Tooltip>
          </S.Row>
          <S.Row>
            <S.ModelNumber isActiveTab={isActive}>
              {getDeviceDisplayModel}
            </S.ModelNumber>
            <S.AUID isActiveTab={isActive}>{device.deviceId}</S.AUID>
          </S.Row>
        </S.Left>
        <S.Right>
          { isHehm
            ? <HealthStatusIcon showToolTip={false} status={device.healthScore as HealthStatus} />
            : <ServiceContractIcon
                displayIcon
                customerName={customerName}
                device={device}
              />
          }
        </S.Right>
      </S.Content>
    </S.Container>
  )
}

export default DeviceTabHeader

const S = {
  Container: styled.div`
    width: 200px;

    @media ${screenSize.small} {
      width: 245px;
    }
  `,
  AlertColor: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isActiveTab', 'severity'].includes(prop)
  })<{ severity: string | null; isActiveTab: boolean }>`    
    width: 100%;
    background-color: ${({ severity, isActiveTab }) => {
    if (severity) {
      const colorTokens = alertSeverityColorMap[severity.toLowerCase()] || alertSeverityColorMap['unknown']
      return color(isActiveTab ? colorTokens.active : colorTokens.inactive)
    } else {
      return 'inherit'
    }
  }};
    border-radius: 2px;
  `,
  Content: styled.div`
    display: flex;
    gap: 2px;
    padding: 5px;
    font-size: 0.85rem;
    text-transform: uppercase;
    justify-content: space-between;
  `,
  Row: styled.div`
    display: flex;
  `,
  Left: styled.div`
    text-align: left;
    margin-left: 5px;
  `,
  Right: styled.div`
    display: flex;
    justify-content: flex-end;
    margin-right: 5px;
    align-items: center;
  `,
  TooltipContent: styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 10px;
  `,
  Label: styled.label`
    margin-right: 5px;
    color: ${color('primaryText')};
    font-weight: bold;
  `,
  Description: styled.span`
    color: ${color('tooltipText')};
  `,
  Zone: styled.div`
    padding: 2px 3px;
  `,
  Name: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isActiveTab'].includes(prop)
  })<{ isActiveTab: boolean }>`
    font-weight: bold;
    font-size: 1rem;
    text-transform: capitalize;
    width: 150px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    cursor: pointer;
    margin-bottom: 5px;
    color: ${color('dashboardDeviceText')};
    font-style: ${({ isActiveTab }) => isActiveTab ? 'italic' : 'normal'};
  `,
  ModelNumber: styled.span.withConfig({
    shouldForwardProp: (prop) => !['isActiveTab'].includes(prop)
  })<{ isActiveTab: boolean }>`
    color: ${color('dashboardDeviceText')};
    font-style: ${({ isActiveTab }) => isActiveTab ? 'italic' : 'normal'};
    min-height: 14.875px;
  `,
  AUID: styled.span.withConfig({
    shouldForwardProp: (prop) => !['isActiveTab'].includes(prop)
  })<{ isActiveTab: boolean }>`
    margin-left: 10px;
    min-height: 14.875px;
    font-weight: 300;
    color: ${color('dashboardDeviceText')};
    font-style: ${({ isActiveTab }) => isActiveTab ? 'italic' : 'normal'};
    user-select: text;
  `,
  ServiceContractIcon: styled.img`
    margin-right: 5px;
    height: 15px;
    transition: all 0.2s ease;
  `,
  AlertsWrapper: styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `,
  AlertsDetails: styled.div`
    display: flex;
    margin-left: -5px;
  `,
  NoActiveAlerts: styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 5px;
    margin-left: 3px;
  `,
  NoAlertIcon: styled.img`
    height: 14px;
    width: 14px;
    transition: all 0.2s ease;
    margin-top: -2px;
  `,
  ServiceContractWrapper: styled.span`
    display: flex;
    align-items: center;
    color: ${color('deviceTooltipLabelText')};
    margin-top: 4px;
    font-weight: bold;
    flex-direction: column;
    margin-left: 3px;
  `,
  ContactInfo: styled.div`
    margin-left: 3px;
  `
}
